import React, { FunctionComponent, useContext } from 'react'

import { noop } from 'lodash-es'
import { CSSObject } from 'styled-components'
import styled, { useTheme } from 'styled-components/native'

import { ContactCareFlowType } from '@lyrahealth-inc/shared-app-logic/src/features/searchForCare/constants'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { AddIcon } from '../../atoms/icons/AddIcon'
import { AngleRightSmallIcon } from '../../atoms/icons/AngleRightSmallIcon'
import { ChevronIcon, ChevronIconDirection } from '../../atoms/icons/ChevronIcon'
import { ExternalLinkIcon } from '../../atoms/icons/ExternalLinkIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { IS_WEB } from '../../constants'
import { AppContext } from '../../context'
import { LatestMatchesLink, LatestMatchesLinkProps } from '../../molecules/latestMatchesLink/LatestMatchesLink'
import { ProgramDetailsContent, ProgramDetailsModal } from '../../organisms/programDetailsModal/ProgramDetailsModal'
import { BodyTextSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { ColumnView, RowView } from '../content/CommonViews'

export interface CareOptionCardProps {
  onPress: (() => void) | undefined
  optionKey?: string
  optionIcon?: React.ReactNode
  optionHeader?: string | React.ReactNode
  optionDescription?: string | React.ReactNode
  testId?: string
  customStyle?: CSSObject
  customHeaderText?: React.ReactNode
  customBodyText?: React.ReactNode
  chevronSize?: number
  latestMatchesLinkProps?: LatestMatchesLinkProps
  ctaText?: React.ReactNode
  /** Opens ProgramDetailsModal instead of triggering `onPress` directly */
  programDetailsModalContent?: ProgramDetailsContent
  useAngleRightSmallIcon?: boolean
  useAddIcon?: boolean
  useExternalLinkIcon?: boolean
  tooltipContent?: string
  useBorderStyles?: boolean
  hideTrailingIcon?: boolean
  flowType?: ContactCareFlowType
  alignIconWithHeader?: boolean
}

const CardContainer = styled.View<{
  theme: ThemeType
  isDedicatedCareNavigatorEnabledAndDefaultFlow?: boolean
  customStyle?: CSSObject
  useBorderStyles?: boolean
}>(({ theme, isDedicatedCareNavigatorEnabledAndDefaultFlow, customStyle, useBorderStyles }) => ({
  backgroundColor:
    isDedicatedCareNavigatorEnabledAndDefaultFlow && !useBorderStyles
      ? undefined
      : theme.isAuroraTheme
        ? theme.colors.cardBackgroundPrimary
        : theme.colors.backgroundPrimary,
  borderColor:
    isDedicatedCareNavigatorEnabledAndDefaultFlow && !useBorderStyles ? undefined : theme.colors.borderDefault,
  borderRadius: isDedicatedCareNavigatorEnabledAndDefaultFlow && !useBorderStyles ? undefined : theme.spacing['8px'],
  borderStyle: isDedicatedCareNavigatorEnabledAndDefaultFlow && !useBorderStyles ? undefined : 'solid',
  borderWidth: isDedicatedCareNavigatorEnabledAndDefaultFlow && !useBorderStyles ? undefined : '1px',
  minHeight: '72px',
  paddingTop: theme.spacing['16px'],
  paddingRight: theme.spacing['16px'],
  paddingBottom: theme.spacing['16px'],
  paddingLeft: isDedicatedCareNavigatorEnabledAndDefaultFlow && !useBorderStyles ? undefined : theme.spacing['16px'],
  flexGrow: 1,
  justifyContent: 'center',
  ...customStyle,
}))

const ContentContainer = styled(RowView)<{ theme: ThemeType }>({
  alignItems: 'center',
})

const HeaderContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginBottom: theme.spacing['4px'],
}))

const BodyContainer = styled(ColumnView)({
  flex: 'auto',
})

const IconContainer = styled.View<{
  theme: ThemeType
  alignIconWithHeader?: boolean
}>(({ theme, alignIconWithHeader }) => ({
  alignSelf: alignIconWithHeader ? 'flex-start' : 'center',
  marginRight: theme.spacing['16px'],
}))

const ChevronContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginLeft: theme.spacing['16px'],
}))

const CardPressable = styled(PressableOpacity)({
  ...(IS_WEB && { height: '100%' }),
})

export const CareOptionCard: FunctionComponent<CareOptionCardProps> = ({
  onPress = noop,
  optionIcon,
  optionHeader,
  optionDescription,
  testId,
  customStyle,
  customHeaderText,
  customBodyText,
  chevronSize,
  latestMatchesLinkProps,
  programDetailsModalContent,
  useAngleRightSmallIcon,
  useAddIcon,
  useExternalLinkIcon,
  useBorderStyles = false,
  hideTrailingIcon = false,
  flowType = ContactCareFlowType.DEFAULT,
  alignIconWithHeader = false,
}) => {
  const { colors } = useTheme() as ThemeType
  const { isDedicatedCareNavigatorEnabled } = useContext(AppContext)
  const isDedicatedCareNavigatorEnabledAndDefaultFlow =
    isDedicatedCareNavigatorEnabled && flowType === ContactCareFlowType.DEFAULT // only apply CN 3.0 styling if the flow types are not neurodiversity or child s4c

  const card = (
    <CardContainer
      customStyle={customStyle}
      isDedicatedCareNavigatorEnabledAndDefaultFlow={isDedicatedCareNavigatorEnabledAndDefaultFlow}
      useBorderStyles={useBorderStyles}
    >
      <ContentContainer>
        {optionIcon && <IconContainer alignIconWithHeader={alignIconWithHeader}>{optionIcon}</IconContainer>}
        <BodyContainer>
          <HeaderContainer>
            {customHeaderText ?? <BodyText text={optionHeader} size={BodyTextSize.DEFAULT} />}
          </HeaderContainer>
          {customBodyText ??
            (optionDescription && (
              <BodyText text={optionDescription} size={BodyTextSize.SMALL} color={colors.textSecondary} />
            ))}
          {latestMatchesLinkProps && <LatestMatchesLink {...latestMatchesLinkProps} />}
        </BodyContainer>
        {!hideTrailingIcon && (
          <ChevronContainer>
            {useExternalLinkIcon ? (
              <ExternalLinkIcon size={chevronSize || 24} strokeColor={colors.iconDefault} />
            ) : useAddIcon ? (
              <AddIcon fillColor={colors.iconDefault} width={chevronSize} />
            ) : useAngleRightSmallIcon ? (
              <AngleRightSmallIcon size={chevronSize} fillColor={colors.iconDefault} />
            ) : (
              <ChevronIcon direction={ChevronIconDirection.RIGHT} fillColor={colors.iconDefault} size={chevronSize} />
            )}
          </ChevronContainer>
        )}
      </ContentContainer>
    </CardContainer>
  )

  if (programDetailsModalContent) {
    return (
      <ProgramDetailsModal
        testId={testId || 'ProgramDetails'}
        modalTrigger={card}
        onCtaPress={onPress}
        programContent={programDetailsModalContent}
      />
    )
  }
  return (
    <CardPressable onPress={onPress} testID={tID(testId)}>
      {card}
    </CardPressable>
  )
}
